import React, { useState } from 'react';
import {
    Box,
    IconButton,
    MenuItem,
    Paper,
    Popover,
    Select,
    TextField,
    Typography,
} from '@mui/material';
import ListIcon from '@mui/icons-material/List';
import SettingsIcon from '@mui/icons-material/Settings';
import DescriptionIcon from '@mui/icons-material/Description';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';

export interface DynacardsModuleProps {
    isCompact?: boolean;
}

const historyCards = [
    { id: 1, date: '13-02-2026', status: 'Aportando', spm: 9.1, stroke: 74, surfPeak: 6.6, surfMin: 0.69, botPeak: 2.92, botMin: -1.46, structural: 0, mpt: 74.45, ept: 72.27, plunger: 0.875, prodBruta: 58.71, eff: 100, fill: 95 },
    { id: 2, date: '03-02-2026', status: 'Aportando', spm: 9.05, stroke: 74, surfPeak: 6.55, surfMin: 0.72, botPeak: 2.85, botMin: -1.3, structural: 0, mpt: 74.1, ept: 71.8, plunger: 0.875, prodBruta: 57.5, eff: 98, fill: 92 },
    { id: 3, date: '27-01-2026', status: 'Golpe de Fluido', spm: 9.15, stroke: 74, surfPeak: 6.8, surfMin: 0.5, botPeak: 2.1, botMin: -1.6, structural: 2, mpt: 75, ept: 70, plunger: 0.875, prodBruta: 45.2, eff: 75, fill: 60 },
    { id: 4, date: '20-01-2026', status: 'Aportando', spm: 8.9, stroke: 73.5, surfPeak: 6.4, surfMin: 0.8, botPeak: 3, botMin: -1.2, structural: 0, mpt: 73, ept: 72, plunger: 0.875, prodBruta: 56, eff: 99, fill: 96 },
];

const surfacePath = '10,220 20,180 40,120 60,90 80,80 100,75 150,70 200,68 250,72 300,80 320,85 340,95 350,110 355,150 350,200 340,240 320,260 280,270 240,275 200,278 150,275 100,270 60,260 30,240 10,220';

const HistoryCard = ({
    card,
    selectedCardId,
    setSelectedCardId,
    surfacePath: path,
    isCompact,
}: {
    card: (typeof historyCards)[0];
    selectedCardId: number;
    setSelectedCardId: (id: number) => void;
    surfacePath: string;
    isCompact: boolean;
}) => {
    const isSelected = selectedCardId === card.id;
    const cartaSize = isCompact ? 64 : 72;
    const cardWidth = isCompact ? 80 : 'auto';
    return (
        <Paper
            elevation={isSelected ? 2 : 0}
            onClick={() => setSelectedCardId(card.id)}
            sx={{
                cursor: 'pointer',
                p: 0.75,
                minWidth: cardWidth,
                width: isCompact ? cardWidth : 'auto',
                flexShrink: 0,
                border: '1px solid',
                borderColor: isSelected ? 'primary.main' : 'grey.200',
                bgcolor: isSelected ? 'primary.50' : 'white',
                borderRadius: 1,
                position: 'relative',
                '&:hover': { borderColor: 'primary.light' },
            }}
        >
            {isSelected && (
                <Box sx={{ position: 'absolute', left: 0, top: 4, bottom: 4, width: 3, bgcolor: 'primary.main', borderRadius: 1 }} />
            )}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                <Typography variant="caption" sx={{ fontSize: 9, fontWeight: 600 }}>{card.date}</Typography>
                <Typography variant="caption" sx={{ fontSize: 8, fontWeight: 700, color: card.status.includes('Golpe') ? 'error.main' : 'success.main' }}>{card.status}</Typography>
            </Box>
            <Box
                sx={{
                    width: cartaSize,
                    height: cartaSize,
                    minWidth: cartaSize,
                    minHeight: cartaSize,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'grey.50',
                    borderRadius: 0.5,
                    overflow: 'hidden',
                }}
            >
                <svg viewBox="0 0 400 300" preserveAspectRatio="xMidYMid meet" style={{ width: '100%', height: '100%', display: 'block' }}>
                    <polyline
                        fill="none"
                        stroke={isSelected ? '#1976d2' : '#94a3b8'}
                        strokeWidth="1.5"
                        points={path}
                    />
                </svg>
            </Box>
        </Paper>
    );
};

const paramsFields = [
    { label: 'SPM', key: 'spm' as const },
    { label: 'Stroke Length', key: 'stroke' as const },
    { label: 'MPT', key: 'mpt' as const },
    { label: 'EPT', key: 'ept' as const },
];

const ParamsGrid = ({
    currentCard,
    prodAjustada,
    twoCol = false,
}: { currentCard: (typeof historyCards)[0]; prodAjustada: string; twoCol?: boolean }) => (
    <Box sx={{ display: 'grid', gridTemplateColumns: twoCol ? '1fr 1fr' : '1fr', gap: 1.5 }}>
        {paramsFields.map((f) => (
            <Box key={f.key}>
                <Typography variant="caption" sx={{ fontSize: 9, fontWeight: 700, color: 'text.secondary', mb: 0.5, display: 'block' }}>{f.label}</Typography>
                <TextField size="small" type="number" defaultValue={currentCard[f.key]} sx={{ '& .MuiInputBase-input': { fontSize: 12 } }} fullWidth />
            </Box>
        ))}
        <Box sx={{ gridColumn: twoCol ? 'span 2' : 1 }}>
            <Typography variant="caption" sx={{ fontSize: 9, fontWeight: 700, color: 'text.secondary', mb: 0.5, display: 'block' }}>Ø Piston</Typography>
            <Select size="small" defaultValue={currentCard.plunger} sx={{ fontSize: 12 }} fullWidth>
                <MenuItem value={0.875}>0.875</MenuItem>
                <MenuItem value={1.25}>1.25</MenuItem>
                <MenuItem value={1.5}>1.50</MenuItem>
            </Select>
        </Box>
        <Box>
            <Typography variant="caption" sx={{ fontSize: 9, fontWeight: 700, color: 'text.secondary', mb: 0.5, display: 'block' }}>Producción Bruta</Typography>
            <TextField size="small" type="number" defaultValue={currentCard.prodBruta} sx={{ '& .MuiInputBase-input': { fontSize: 12 } }} fullWidth />
        </Box>
        <Box>
            <Typography variant="caption" sx={{ fontSize: 9, fontWeight: 700, color: 'text.secondary', mb: 0.5, display: 'block' }}>Eficiencia (%)</Typography>
            <TextField size="small" type="number" defaultValue={currentCard.eff} sx={{ '& .MuiInputBase-input': { fontSize: 12 } }} fullWidth />
        </Box>
        <Box sx={{ gridColumn: twoCol ? 'span 2' : 1, p: 1.5, bgcolor: 'primary.50', borderRadius: 1, border: '1px solid', borderColor: 'primary.100' }}>
            <Typography variant="caption" sx={{ fontSize: 9, fontWeight: 700, color: 'primary.600', mb: 0.5, display: 'block' }}>Producción Ajustada</Typography>
            <Typography variant="h6" fontWeight={700} color="primary.main" sx={{ fontSize: 18 }}>{prodAjustada} <Typography component="span" variant="caption">BPD</Typography></Typography>
        </Box>
    </Box>
);

const generateBottomPath = (fill: number) => {
    const w = 350;
    const h = 150;
    const fillPct = Math.max(0, Math.min(100, fill)) / 100;
    const x = 20 + fillPct * (w - 40);
    return `20,${200 - h} ${x},${200 - h} ${x},200 ${w},200 ${w},200 20,200 20,${200 - h}`;
};

export const DynacardsModule = ({ isCompact = true }: DynacardsModuleProps) => {
    const [selectedCardId, setSelectedCardId] = useState(1);
    const [fillNav, setFillNav] = useState(0);
    const [historialAnchorEl, setHistorialAnchorEl] = useState<HTMLButtonElement | null>(null);
    const [parametrosAnchorEl, setParametrosAnchorEl] = useState<HTMLButtonElement | null>(null);
    const historialOpen = Boolean(historialAnchorEl);
    const parametrosOpen = Boolean(parametrosAnchorEl);
    const currentCard = historyCards.find((c) => c.id === selectedCardId) || historyCards[0];
    const displayFill = Math.max(0, Math.min(100, currentCard.fill + fillNav));

    const metrics = [
        { label: 'Surface Peak Load', value: currentCard.surfPeak },
        { label: 'Surface Min Load', value: currentCard.surfMin },
        { label: 'Bottom Peak Load', value: currentCard.botPeak },
        { label: 'Bottom Min Load', value: currentCard.botMin },
        { label: '% Carga Estructural', value: currentCard.structural },
    ];

    const prodAjustada = (currentCard.prodBruta * (currentCard.eff / 100)).toFixed(2);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden', bgcolor: 'grey.50' }}>
            <Box
                sx={{
                    flex: 1,
                    display: 'flex',
                    overflow: 'hidden',
                    minHeight: 0,
                    flexDirection: isCompact ? 'column' : 'row',
                }}
            >
                {/* Extended: Historial left sidebar primero */}
                {!isCompact && (
                    <Box
                        sx={{
                            flexShrink: 0,
                            width: 140,
                            borderRight: '1px solid',
                            borderColor: 'divider',
                            overflowY: 'auto',
                            display: 'flex',
                            flexDirection: 'column',
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1, mb: 0.5 }}>
                            <ListIcon sx={{ fontSize: 12, color: 'text.secondary' }} />
                            <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ fontSize: 9, textTransform: 'uppercase' }}>Historial</Typography>
                        </Box>
                        <IconButton size="small" sx={{ alignSelf: 'center' }}><ChevronLeftIcon sx={{ fontSize: 16, transform: 'rotate(-90deg)' }} /></IconButton>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, p: 1 }}>
                            {historyCards.map((card) => (
                                <HistoryCard key={card.id} card={card} selectedCardId={selectedCardId} setSelectedCardId={setSelectedCardId} surfacePath={surfacePath} isCompact={false} />
                            ))}
                        </Box>
                        <IconButton size="small" sx={{ alignSelf: 'center' }}><ChevronLeftIcon sx={{ fontSize: 16, transform: 'rotate(90deg)' }} /></IconButton>
                    </Box>
                )}

                {/* Centro: métricas + botones Historial y Parámetros (compact) */}
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, overflow: 'hidden' }}>
                    {/* Métricas + botones Historial (izq) y Parámetros (der) en compact */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.5, bgcolor: 'white', borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
                        {isCompact && (
                                <IconButton
                                    size="small"
                                    onClick={(e) => setHistorialAnchorEl(historialOpen ? null : e.currentTarget)}
                                    sx={{ flexShrink: 0 }}
                                    aria-label="Abrir historial de cartas"
                                    color={historialOpen ? 'primary' : 'default'}
                                >
                                    <ListIcon fontSize="small" />
                                </IconButton>
                        )}
                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 1, flex: 1, minWidth: 0 }}>
                            {metrics.map((m, i) => (
                                <Box key={i}>
                                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: 8, fontWeight: 700, textTransform: 'uppercase' }}>{m.label}</Typography>
                                    <Typography variant="body2" fontWeight={700} sx={{ fontSize: 14 }}>{typeof m.value === 'number' ? m.value.toFixed(2) : m.value}</Typography>
                                </Box>
                            ))}
                        </Box>
                        {isCompact && (
                            <IconButton
                                size="small"
                                onClick={(e) => setParametrosAnchorEl(parametrosOpen ? null : e.currentTarget)}
                                sx={{ flexShrink: 0 }}
                                aria-label="Abrir parámetros operativos"
                                color={parametrosOpen ? 'primary' : 'default'}
                            >
                                <SettingsIcon fontSize="small" />
                            </IconButton>
                        )}
                    </Box>
                    {/* Gráficas (centro) */}
                    <Box sx={{ flex: 1, overflow: 'auto', p: 1.5, display: 'flex', flexDirection: 'column', gap: 2, minHeight: 0, position: 'relative' }}>
                        <Paper elevation={0} sx={{ p: 1.5, border: '1px solid', borderColor: 'grey.200', borderRadius: 2, flex: 1, minHeight: isCompact ? 260 : 240, display: 'flex', flexDirection: 'column' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexShrink: 0 }}>
                                <Box sx={{ bgcolor: 'primary.main', color: 'white', px: 1.5, py: 0.5, borderRadius: 1 }}>
                                    <Typography variant="caption" fontWeight={700} sx={{ fontSize: 9, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <DescriptionIcon sx={{ fontSize: 10 }} /> Carta de Superficie
                                    </Typography>
                                </Box>
                            </Box>
                            <Box sx={{ flex: 1, minHeight: isCompact ? 240 : 200, position: 'relative' }}>
                                <Box sx={{ position: 'absolute', top: '18%', left: 0, right: 0, borderTop: '2px dashed', borderColor: 'error.main', opacity: 0.6 }} />
                                <Typography sx={{ position: 'absolute', top: '16%', right: 8, fontSize: 9, fontWeight: 700, color: 'error.main', bgcolor: 'white' }}>Wrf + Fo Max</Typography>
                                <Box sx={{ position: 'absolute', top: '30%', left: 0, right: 0, borderTop: '2px dashed', borderColor: 'grey.800', opacity: 0.6 }} />
                                <Typography sx={{ position: 'absolute', top: '28%', right: 8, fontSize: 9, fontWeight: 700, bgcolor: 'white' }}>Wrf + Fo FL</Typography>
                                <Box sx={{ position: 'absolute', bottom: '20%', left: 0, right: 0, borderTop: '2px dashed', borderColor: 'grey.800', opacity: 0.6 }} />
                                <Typography sx={{ position: 'absolute', bottom: '22%', left: 8, fontSize: 9, fontWeight: 700, bgcolor: 'white' }}>Wrf</Typography>
                                <Box component="svg" viewBox="0 0 400 300" preserveAspectRatio="xMidYMid meet" sx={{ width: '100%', height: '100%' }}>
                                    {[...Array(6)].map((_, i) => <line key={i} x1="0" y1={i * 50} x2="400" y2={i * 50} stroke="#f1f5f9" />)}
                                    {[...Array(8)].map((_, i) => <line key={i} x1={i * 50} y1="0" x2={i * 50} y2="300" stroke="#f1f5f9" />)}
                                    <polyline fill="none" stroke="#1976d2" strokeWidth="2.5" points={surfacePath} />
                                </Box>
                            </Box>
                        </Paper>
                        <Paper elevation={0} sx={{ p: 1.5, border: '1px solid', borderColor: 'grey.200', borderRadius: 2, flex: 1, minHeight: 200, display: 'flex', flexDirection: 'column' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexShrink: 0 }}>
                                <Box sx={{ bgcolor: 'primary.main', color: 'white', px: 1.5, py: 0.5, borderRadius: 1 }}>
                                    <Typography variant="caption" fontWeight={700} sx={{ fontSize: 9 }}>Carta de Fondo</Typography>
                                </Box>
                                <Box sx={{ bgcolor: 'grey.900', color: 'white', px: 1.5, py: 0.5, borderRadius: 1 }}>
                                    <Typography variant="caption" fontWeight={700} sx={{ fontSize: 9 }}>EPT</Typography>
                                </Box>
                            </Box>
                            <Box sx={{ flex: 1, minHeight: isCompact ? 180 : 160, position: 'relative' }}>
                                <Box sx={{ position: 'absolute', top: '25%', left: 0, right: 0, borderTop: '2px dashed', borderColor: 'error.main', opacity: 0.6 }} />
                                <Typography sx={{ position: 'absolute', top: '23%', right: 8, fontSize: 9, fontWeight: 700, color: 'error.main', bgcolor: 'white' }}>Fo max</Typography>
                                <Box sx={{ position: 'absolute', top: '35%', left: 0, right: 0, borderTop: '2px dashed', borderColor: 'grey.800', opacity: 0.6 }} />
                                <Typography sx={{ position: 'absolute', top: '33%', right: 8, fontSize: 9, fontWeight: 700, bgcolor: 'white' }}>Fo</Typography>
                                <Box sx={{ position: 'absolute', bottom: '10%', left: 0, right: 0, borderTop: '2px dashed', borderColor: 'grey.800', opacity: 0.6 }} />
                                <Typography sx={{ position: 'absolute', bottom: '12%', left: 8, fontSize: 9, fontWeight: 700, bgcolor: 'white' }}>Cero line</Typography>
                                <Box component="svg" viewBox="0 0 400 200" preserveAspectRatio="xMidYMid meet" sx={{ width: '100%', height: '100%' }}>
                                    {[...Array(4)].map((_, i) => <line key={i} x1="0" y1={i * 50} x2="400" y2={i * 50} stroke="#f1f5f9" />)}
                                    {[...Array(8)].map((_, i) => <line key={i} x1={i * 50} y1="0" x2={i * 50} y2="200" stroke="#f1f5f9" />)}
                                    <polyline fill="none" stroke="#1976d2" strokeWidth="2.5" points={generateBottomPath(displayFill)} />
                                </Box>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1, pt: 1, borderTop: '1px solid', borderColor: 'grey.100' }}>
                                <Typography variant="caption" sx={{ fontSize: 9 }}>0</Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, bgcolor: 'grey.100', px: 1, py: 0.5, borderRadius: 1 }}>
                                    <IconButton size="small" onClick={() => setFillNav((v) => Math.max(-10, v - 5))}><ChevronLeftIcon sx={{ fontSize: 14 }} /></IconButton>
                                    <Typography variant="caption" fontWeight={700} sx={{ fontSize: 9 }}>Llenado: {displayFill}%</Typography>
                                    <IconButton size="small" onClick={() => setFillNav((v) => Math.min(10, v + 5))}><ChevronRightIcon sx={{ fontSize: 14 }} /></IconButton>
                                </Box>
                                <Typography variant="caption" sx={{ fontSize: 9 }}>80</Typography>
                            </Box>
                        </Paper>
                    </Box>
                </Box>

                {/* Popover Historial flotante (compact): desde la izquierda sobre la carta */}
                {isCompact && (
                    <Popover
                        open={historialOpen}
                        anchorEl={historialAnchorEl}
                        onClose={() => setHistorialAnchorEl(null)}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                        slotProps={{ paper: { sx: { mt: 0.5, boxShadow: 3, borderRadius: 2, overflow: 'hidden', maxHeight: '70vh' } } }}
                    >
                        <Box sx={{ width: 140, p: 1, bgcolor: 'white', maxHeight: '70vh', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                                <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ fontSize: 9, textTransform: 'uppercase' }}>Historial</Typography>
                                <IconButton size="small" onClick={() => setHistorialAnchorEl(null)} sx={{ p: 0.25 }} aria-label="Cerrar historial">
                                    <CloseIcon sx={{ fontSize: 14 }} />
                                </IconButton>
                            </Box>
                            {historyCards.map((card) => (
                                <HistoryCard key={card.id} card={card} selectedCardId={selectedCardId} setSelectedCardId={setSelectedCardId} surfacePath={surfacePath} isCompact={false} />
                            ))}
                        </Box>
                    </Popover>
                )}
                {/* Popover Parámetros flotante (compact): desde la derecha sobre la carta, más ancho */}
                {isCompact && (
                    <Popover
                        open={parametrosOpen}
                        anchorEl={parametrosAnchorEl}
                        onClose={() => setParametrosAnchorEl(null)}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                        slotProps={{ paper: { sx: { mt: 0.5, boxShadow: 3, borderRadius: 2, overflow: 'hidden', maxHeight: '70vh', width: 240 } } }}
                    >
                        <Box sx={{ p: 1.5, bgcolor: 'white', maxHeight: '70vh', overflowY: 'auto' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                                <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ fontSize: 9, textTransform: 'uppercase' }}>Parámetros Operativos</Typography>
                                <IconButton size="small" onClick={() => setParametrosAnchorEl(null)} sx={{ p: 0.25 }} aria-label="Cerrar parámetros">
                                    <CloseIcon sx={{ fontSize: 14 }} />
                                </IconButton>
                            </Box>
                            <ParamsGrid currentCard={currentCard} prodAjustada={prodAjustada} />
                        </Box>
                    </Popover>
                )}

                {/* Parámetros - Solo en modo extendido (sidebar derecha); en compact se muestra vía tabs */}
                {!isCompact && (
                    <Box sx={{ flexShrink: 0, width: 180, borderLeft: '1px solid', borderColor: 'divider', overflowY: 'auto', p: 1.5, bgcolor: 'white' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1.5 }}>
                            <SettingsIcon sx={{ fontSize: 12, color: 'text.secondary' }} />
                            <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ fontSize: 9, textTransform: 'uppercase' }}>Parámetros Operativos</Typography>
                        </Box>
                        <ParamsGrid currentCard={currentCard} prodAjustada={prodAjustada} />
                    </Box>
                )}
            </Box>
        </Box>
    );
};
